import { useState, useCallback, useMemo } from 'react';

/**
 * Donation panel state management hook.
 * Adapted from Magic Forest useModalState — modal shell removed.
 * @param {Function} onBack — callback to return to editor
 * @param {string} [userEmail] — prefill email from QooQoo auth
 */
export const useDonationState = (onBack, userEmail) => {
  const [donationStep, setDonationStep] = useState(1);
  const [donationType, setDonationType] = useState('one-time');
  const [donationAmount, setDonationAmount] = useState(10);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustomAmountManuallyEdited, setIsCustomAmountManuallyEdited] = useState(false);
  const [donorEmail, setDonorEmail] = useState(userEmail || '');
  const [donorName, setDonorName] = useState('');

  const [gdprConsent, setGdprConsent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const [receiptEmail, setReceiptEmail] = useState(userEmail || '');
  const [isReceiptSending, setIsReceiptSending] = useState(false);
  const [isReceiptSent, setIsReceiptSent] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const closeDonationPanel = useCallback(() => {
    if (onBack) onBack();
  }, [onBack]);

  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= 3) setDonationStep(step);
  }, []);

  const resetState = useCallback(() => {
    setDonationStep(1);
    setDonationType('one-time');
    setDonationAmount(10);
    setCustomAmount('');
    setIsCustomAmountManuallyEdited(false);
    setDonorEmail(userEmail || '');
    setDonorName('');
    setGdprConsent(true);
    setIsSubmitting(false);
    setIsSuccess(false);
    setSelectedPaymentMethod(null);
    setPaymentIntentId(null);
    setReceiptEmail(userEmail || '');
    setIsReceiptSending(false);
    setIsReceiptSent(false);
    setSubscriptionInfo(null);
  }, [userEmail]);

  return useMemo(() => ({
    donationStep, donationType, donationAmount, customAmount, isCustomAmountManuallyEdited,
    donorEmail, donorName, gdprConsent, isSubmitting, isSuccess,
    paymentIntentId, selectedPaymentMethod,
    receiptEmail, isReceiptSending, isReceiptSent, subscriptionInfo,
    setDonationType, setDonationAmount, setCustomAmount, setIsCustomAmountManuallyEdited,
    setDonorEmail, setDonorName, setGdprConsent, setIsSubmitting, setIsSuccess,
    setPaymentIntentId, setSelectedPaymentMethod,
    setReceiptEmail, setIsReceiptSending, setIsReceiptSent, setSubscriptionInfo,
    goToStep, closeDonationPanel, resetState,
  }), [
    donationStep, donationType, donationAmount, customAmount, isCustomAmountManuallyEdited,
    donorEmail, donorName, gdprConsent, isSubmitting, isSuccess,
    paymentIntentId, selectedPaymentMethod,
    receiptEmail, isReceiptSending, isReceiptSent, subscriptionInfo,
    goToStep, closeDonationPanel, resetState,
  ]);
};
